'use client';

import dynamic from 'next/dynamic';
import { Container } from '@/components/common/container';
import type {
  CompanyBasicResponse,
  CompanyNetworkResponse,
} from '@/lib/backend-api';
import type { TopEmployerRankLink } from '@/lib/seo-lists/types';

// ---------------------------------------------------------------------------
// Lazy-loaded heavy chart components (recharts / XyFlow) — defer until visible.
// Loading skeletons match the final rendered heights to avoid CLS.
// ---------------------------------------------------------------------------
const ChartSkeleton = () => <div className="h-[315px]" />;
const CostsSkeleton = () => <div className="h-[420px]" />;

export const CompanyTrendChart = dynamic(
  () => import('./CompanyTrendChart').then((mod) => mod.CompanyTrendChart),
  { ssr: false, loading: ChartSkeleton },
);

export const CompanyCostsPieChart = dynamic(
  () =>
    import('./CompanyCostsPieChart').then((mod) => mod.CompanyCostsPieChart),
  { ssr: false, loading: CostsSkeleton },
);

// ---------------------------------------------------------------------------
// Mobile ordering
// ---------------------------------------------------------------------------
/**
 * The mobile reading order, and the only place it is defined.
 *
 * Desktop is a 2fr/1fr grid whose two columns each flow top-down, and the
 * mobile stack used to be the concatenation of those two columns — main column
 * first, sidebar after. Both columns lead with their most important card, so
 * everything in the sidebar landed below everything in the main column:
 * measured on a 390px viewport (Mercedes-Benz Group AG, guest), the financial
 * chart started at 2,438px and the Jahresabschlüsse — the cards users actually
 * pay credits for — at 4,082px of a 4,543px page. Effectively the bottom.
 *
 * The fix is CSS-only. Below `lg` both column wrappers are `display: contents`,
 * which erases their boxes and promotes every card to a direct child of the
 * single-column grid; the `order-*` values below then lay them out in an order
 * that owes nothing to the desktop columns. At `lg` the wrappers become flex
 * columns again and `lg:order-none` resets every card, so the desktop layout is
 * byte-for-byte what it was.
 *
 * Why not a `useIsMobile()` branch rendering two trees: the DOM order here
 * never changes, so the SSR HTML (which is what guests and crawlers get) is
 * untouched, there is no hydration mismatch, and heavy children — the XyFlow
 * network diagram above all — mount exactly once.
 *
 * Rules when editing:
 * - Tailwind only ships `order-1`..`order-12`; beyond that use the arbitrary
 *   form (`order-[13]`). Values must be written as literal strings so the JIT
 *   sees them — never build a class name at runtime.
 * - Every card gets `border-b border-border` from here rather than from a
 *   `[&>*]` rule on the wrapper: with `display: contents` the wrapper is not a
 *   box, so `[&>*:last-child]:border-b-0` would key off DOM position while the
 *   user sees `order` position.
 * - `min-w-0 overflow-hidden` likewise moves onto the cards. It used to sit on
 *   the left wrapper and is what stops a wide table (tenders, admin financials)
 *   from pushing the page sideways; `display: contents` would drop that guard.
 */
const CARD_BASE =
  'border-b border-border min-w-0 overflow-hidden lg:order-none';
export const MOBILE_ORDER = {
  /** The profile card as one box (desktop); below lg it splits in two, see
   *  profileTop / profileRest. */
  profile: CARD_BASE,
  /** Identity, KPI row, About, NACE. */
  profileTop: `${CARD_BASE} order-1`,
  /** Bilanz viewer & download. Monetised (credits) — right after the summary. */
  financialDocs: `${CARD_BASE} order-2`,
  /** AD/CD/DK register files. Monetised, and adjacent to the Bilanz card. */
  registerDocs: `${CARD_BASE} order-3`,
  /** The profile card's rest: network diagram, business branches, product/risk tags. */
  profileRest: `${CARD_BASE} order-4`,
  /** Revenue/profit/employees trend — the trajectory the KPI row can't show. */
  financialsChart: `${CARD_BASE} order-5`,
  /** Managers / Gesellschafter (gated) / Kommanditisten / Beteiligungen. */
  people: `${CARD_BASE} order-6`,
  /** Website, socials, email (gated). */
  contact: `${CARD_BASE} order-7`,
  /** Event timeline (blurred rows for guests). */
  history: `${CARD_BASE} order-8`,
  costs: `${CARD_BASE} order-9`,
  zeitverlauf: `${CARD_BASE} order-10`,
  /* The standalone tenders card used to hold order-11; tenders are now a tab
     on the history card, and funding took the freed slot. */
  funding: `${CARD_BASE} order-11`,
  /** Last: the only card that navigates away — the exit, not an off-ramp. */
  peers: `${CARD_BASE} order-12`,
  adminFinancials: `${CARD_BASE} order-[13]`,
  adminValidation: `${CARD_BASE} order-[14]`,
  adminActions: `${CARD_BASE} order-[15]`,
} as const;

/** Scroll targets for {@link MobileActionBar}; set as element ids on the cards. */
export const FINANCIAL_DOCS_ANCHOR = 'jahresabschluesse';
export const REGISTER_DOCS_ANCHOR = 'registerdateien';

/** CompanyDetailPage's render while the client-side basic-info fetch runs. */
export function CompanyDetailLoading() {
  return (
    <div id="company-detail-print" className="bg-background fd-scale-xl">
      <Container className="flex flex-col gap-6 py-8 lg:gap-7.5">
        <div className="text-center text-muted-foreground">
          Loading company data...
        </div>
      </Container>
    </div>
  );
}

export type CompanyDetailPageProps = {
  euId: string;
  /** When provided (from SSR), the client skips the basic-info fetch entirely. */
  basicData?: CompanyBasicResponse | null;
  /** Pre-fetched network graph data from SSR — passed directly to the diagram. */
  networkData?: CompanyNetworkResponse | null;
  /** SSR: the Top-Arbeitgeber page this company ranks on, if any. */
  topEmployerRank?: TopEmployerRankLink | null;
};
